import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { API } from '../utils/api';

// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// XHR helper that avoids platform fetch interception
const xhrRequest = (method, url, options = {}) => {
  return new Promise((resolve, reject) => {
    const { body, headers = {}, withCredentials = false } = options;
    const x = new XMLHttpRequest();
    x.open(method, url, true);
    if (withCredentials) x.withCredentials = true;
    Object.entries(headers).forEach(([k, v]) => x.setRequestHeader(k, v));
    if (body) x.setRequestHeader('Content-Type', 'application/json');
    x.onload = function() {
      let data = null;
      try { data = JSON.parse(x.responseText); } catch (e) { data = x.responseText; }
      resolve({ ok: x.status >= 200 && x.status < 300, status: x.status, data });
    };
    x.onerror = function() { reject(new Error('Network error')); };
    x.ontimeout = function() { reject(new Error('Request timeout')); };
    x.timeout = 15000;
    x.send(body ? JSON.stringify(body) : null);
  });
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Refresh token function - tries to get new access token using httpOnly cookie
  const refreshAccessToken = useCallback(async () => {
    try {
      const res = await xhrRequest('POST', `${API}/auth/refresh`, {
        withCredentials: true
      });
      if (res.ok) {
        localStorage.setItem('token', res.data.token);
        setToken(res.data.token);
        setUser(res.data.user);
        return true;
      }
      return false;
    } catch (e) {
      console.error('Token refresh failed:', e);
      return false;
    }
  }, []);

  // Check if we have a valid refresh token on startup
  const checkRefreshToken = useCallback(async () => {
    try {
      const res = await xhrRequest('GET', `${API}/auth/check-refresh`, {
        withCredentials: true
      });
      return res.ok && res.data?.valid;
    } catch (e) {
      console.error('Refresh token check failed:', e);
      return false;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=') || 
        window.location.search?.includes('session_id=') ||
        window.location.href?.match(/session_id=[^&#]+/)) {
      setLoading(false);
      return;
    }
    
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      // First, try to validate the stored token
      if (storedToken) {
        try {
          const res = await xhrRequest('GET', `${API}/auth/me`, {
            headers: { 'Authorization': `Bearer ${storedToken}` }
          });
          if (res.ok) {
            setUser(res.data);
            setToken(storedToken);
            setLoading(false);
            return;
          }
        } catch (e) {
          console.error('Auth check network error:', e);
        }
      }
      
      // If no valid stored token, try to refresh using httpOnly cookie
      const hasRefreshToken = await checkRefreshToken();
      if (hasRefreshToken) {
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          setLoading(false);
          return;
        }
      }
      
      // No valid session - clear any stale data
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
      setLoading(false);
    };
    
    initAuth();
  }, [checkRefreshToken, refreshAccessToken]);

  // Set up token refresh interval (refresh before expiry)
  useEffect(() => {
    if (!token) return;
    
    // Refresh token every 20 minutes (before the 24-hour access token expires)
    // Guard: only call refresh if a valid refresh cookie exists
    const refreshInterval = setInterval(async () => {
      const hasRefresh = await checkRefreshToken();
      if (hasRefresh) {
        await refreshAccessToken();
      }
    }, 20 * 60 * 1000); // 20 minutes
    
    return () => clearInterval(refreshInterval);
  }, [token, refreshAccessToken]);

  const login = async (email, password, rememberMe = false) => {
    const res = await xhrRequest('POST', `${API}/auth/login?remember_me=${rememberMe}`, {
      body: { email, password },
      withCredentials: true
    });
    
    if (!res.ok) {
      throw new Error(res.data?.detail || 'Login failed');
    }
    
    localStorage.setItem('token', res.data.token);
    if (rememberMe) localStorage.setItem('remember_me', 'true');
    else localStorage.removeItem('remember_me');
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, username, password, isEighteenOrOlder = false, rememberMe = false) => {
    const res = await xhrRequest('POST', `${API}/auth/register?remember_me=${rememberMe}`, {
      body: { email, username, password, is_eighteen_or_older: isEighteenOrOlder },
      withCredentials: true
    });
    
    if (!res.ok) {
      throw new Error(res.data?.detail || 'Registration failed');
    }
    
    localStorage.setItem('token', res.data.token);
    if (rememberMe) localStorage.setItem('remember_me', 'true');
    else localStorage.removeItem('remember_me');
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  // Google OAuth login using Emergent Auth
  const loginWithGoogle = async (googleUserData) => {
    const res = await xhrRequest('POST', `${API}/auth/google`, {
      body: {
        email: googleUserData.email,
        google_id: googleUserData.id,
        name: googleUserData.name,
        picture: googleUserData.picture
      },
      withCredentials: true
    });
    
    if (!res.ok) {
      throw new Error(res.data?.detail || 'Google login failed');
    }
    
    localStorage.setItem('token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try {
      await xhrRequest('POST', `${API}/auth/logout`, {
        withCredentials: true,
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (e) {
      console.error('Logout request failed:', e);
    }
    
    // Clear local state regardless of API call success
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const res = await xhrRequest('GET', `${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setUser(res.data);
      } catch (e) {
        console.error('Refresh user failed:', e);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      loginWithGoogle, 
      logout, 
      refreshUser, 
      refreshAccessToken,
      setUser, 
      setToken, 
      setLoading 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
